import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

export type TApplicationField = {
  id: number;
  uid: string;
  type: string;
  name: string;
  label: string;
  format?: string;
  active?: boolean;
  updatedAt: string;
  validator?: string;
  mandatory?: boolean;
  labelHindi?: string;
  placeholder?: string;
  labelMarathi?: string;
  /** Shown with its value but cannot be changed; sent by the reviewer's Edit Application page. */
  readOnly?: boolean;
  /** Ids of the forms the field is mapped to; sent by /applications/all-fields. */
  formIds?: number[];
};

export type TApplicationFieldOption = {
  id: number;
  name: string;
  active?: boolean;
};

const applicationFieldsKeyStore = {
  applicationFieldOptions: (id: number) => ["application-field-options", id],
  applicationFieldsCount: (...args: (number | string)[]) => [
    "application-fields-count",
    ...args,
  ],
  applicationFields: (...args: (number | string)[]) => [
    "application-fields",
    ...args,
  ],
};

export function useApplicationFields({
  limit,
  page,
  search,
  sort,
  sortVal,
  filters,
  lastDate,
}: {
  page: number;
  limit: number;
  search?: string;
  sort?: string;
  sortVal?: number;
  filters?: string;
  lastDate?: string | null;
}) {
  return useQuery({
    queryFn: () =>
      _axios.get<{
        success: true;
        hasMore?: boolean;
        data: Array<TApplicationField>;
      }>("/application-fields", {
        params: { limit, page, search, sort, sortVal, filters, lastDate },
      }),
    queryKey: applicationFieldsKeyStore.applicationFields(
      page,
      limit,
      search || "",
      sort || "",
      sortVal || 1,
      filters || "",
      lastDate || ""
    ),
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useApplicationFieldsCount({
  search,
  filters,
  lastDate,
}: {
  search?: string;
  filters?: string;
  lastDate?: string | null;
}) {
  return useQuery({
    queryFn: () =>
      _axios.get<{ success: true; data: number }>("/application-fields/count", {
        params: { search, filters, lastDate },
      }),
    queryKey: applicationFieldsKeyStore.applicationFieldsCount(
      search || "",
      filters ?? "",
      lastDate ?? ""
    ),
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useApplicationFieldOptions(id: number) {
  return useQuery({
    queryFn: () =>
      _axios.get<{
        success: true;
        hasMore?: boolean;
        data?: TApplicationFieldOption[];
      }>(`/application-fields/${id}/options`),
    enabled: !!id,
    queryKey: applicationFieldsKeyStore.applicationFieldOptions(id),
    select: (result) => result.data,
  });
}

export function usePatchApplicationField() {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean },
    AxiosError | Error,
    {
      id: number;
      label?: string;
      placeholder?: string;
      active?: boolean;
      mandatory?: boolean;
      options?: number[];
      format?: string;
    }
  >({
    mutationFn: ({ id, ...rest }) =>
      _axios
        .patch<{ success: boolean }>(`/application-fields/${id}`, rest)
        .then((res) => res.data),
    onError: () => {
      ErrorToast("Something went wrong while updating field!");
    },
    onSuccess: () => {
      SuccessToast("Field updated successfully!");
      return qc.invalidateQueries({
        queryKey: applicationFieldsKeyStore.applicationFields(),
      });
    },
  });
}

export function useAddNewOption() {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean; optionId: number },
    AxiosError | Error,
    { fieldId: number; name?: string }
  >({
    mutationFn: ({ fieldId, ...rest }) =>
      _axios
        .post<{ success: boolean; optionId: number }>(
          `/application-fields/${fieldId}/options`,
          rest
        )
        .then((res) => res.data),
    onError: (err) => {
      if (err instanceof AxiosError && err?.response?.data?.message) {
        ErrorToast(err?.response?.data?.message);
      } else ErrorToast("Something went wrong while adding new option!");
    },
    onSuccess: (_, { fieldId }) => {
      SuccessToast("Option added successfully!");
      return qc.invalidateQueries({
        queryKey: applicationFieldsKeyStore.applicationFieldOptions(fieldId),
      });
    },
  });
}
